import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { X, ArrowRight, Check } from "lucide-react";
import { useLead } from "@/context/LeadContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LeadModal = () => {
  const { open, meta, closeLead } = useLead();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("solo");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        email,
        name: name || null,
        role,
        plan: meta.plan,
        source: meta.source,
      });
      setDone(true);
      toast.success("You're on the list. Check your inbox.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    closeLead();
    setTimeout(() => {
      setDone(false);
      setEmail("");
      setName("");
    }, 300);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="lead-modal">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-ink/60 backdrop-blur-sm"
        onClick={reset}
      />
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md bg-bone border border-ink/10 shadow-2xl"
      >
        <button
          onClick={reset}
          className="absolute top-4 right-4 text-ink/50 hover:text-ink transition-colors"
          data-testid="lead-modal-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {done ? (
          <div className="p-10 text-center" data-testid="lead-modal-success">
            <div className="mx-auto w-14 h-14 flex items-center justify-center bg-coral text-bone rounded-full mb-6">
              <Check className="w-7 h-7" />
            </div>
            <h3 className="font-serif text-3xl text-ink mb-3">You&apos;re in.</h3>
            <p className="text-ink/70 text-sm leading-relaxed mb-8">
              Your free trial is reserved. We&apos;ll email setup instructions so your first listing
              goes live in minutes.
            </p>
            <button
              onClick={reset}
              className="w-full bg-ink text-bone py-3 text-sm font-medium hover:bg-coral transition-colors"
              data-testid="lead-modal-done"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-8 sm:p-10" data-testid="lead-form">
            <p className="text-xs uppercase tracking-[0.15em] text-coral mb-3">Free · 14 days</p>
            <h3 className="font-serif text-3xl sm:text-4xl text-ink leading-none mb-3">
              Start your free trial
            </h3>
            <p className="text-ink/60 text-sm mb-8">No credit card. Cancel anytime.</p>

            <label className="block text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              data-testid="lead-input-name"
              className="w-full bg-transparent border border-ink/20 px-4 py-3 text-ink placeholder:text-ink/40 focus:border-coral focus:outline-none transition-colors mb-5"
            />

            <label className="block text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Email *</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@brokerage.com"
              data-testid="lead-input-email"
              className="w-full bg-transparent border border-ink/20 px-4 py-3 text-ink placeholder:text-ink/40 focus:border-coral focus:outline-none transition-colors mb-5"
            />

            <label className="block text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">I am a</label>
            <div className="grid grid-cols-2 gap-2 mb-8">
              {[
                { id: "solo", label: "Solo agent" },
                { id: "brokerage", label: "Brokerage" },
              ].map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRole(r.id)}
                  data-testid={`lead-role-${r.id}`}
                  className={`py-3 text-sm border transition-colors ${
                    role === r.id
                      ? "bg-ink text-bone border-ink"
                      : "border-ink/20 text-ink/70 hover:border-ink"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="lead-submit"
              className="group w-full inline-flex items-center justify-center gap-2 bg-coral text-bone py-3.5 text-sm font-medium hover:bg-ink transition-colors disabled:opacity-60"
            >
              {loading ? "Reserving…" : "Claim my trial"}
              {!loading && (
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              )}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
};
